/**
 * @module survey.actions
 */
import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { normalize } from 'normalizr';
import * as excel from '~/adapters/excel';
import { graphqlRequest, hasGraphQLErrors, hasGraphQLNetworkError } from '~/adapters/graphqlRequest';
import { httpRequest } from '~/adapters/httpRequest';
import { host } from '~/config/settings/worktango.settings';
import { typeNames } from '~/config/settings/survey_element.settings';
import { statuses } from '~/config/settings/notification.settings';
import * as languages from '../languages';
import * as transformers from '../transformers';
import * as schemas from '../schemas';
import {
  CreateSurveyInput,
  MigratedSurvey,
  SurveyConfig,
  SurveyDict,
  TestSurveyResult,
  SurveyElementToExport,
  UpdateSurveyInput,
  UpdateSurveyStatusInput,
} from '~/interfaces/survey.interface';
import { RootState } from '../store';
import * as l10n from '../languages';
import {
  getMigrationSurveyData,
  validateSurveyMetadata,
} from '~/views/Main.section/Surveys.section/Build.section/MigrateSurvey/surveyMigration.service';
import { text } from '~/tools/text';
import { convert } from '~/tools/convert';
import {
  ApproveMigratedSurveyDocument,
  ApproveMigratedSurveyMutationResult,
  ConvertSurveyToContinuousDocument,
  DeleteSurveyDocument,
  DeleteSurveyMutationResult,
  DisableOpenSurveyAccessCodeDocument,
  DisableOpenSurveyAccessCodeMutationResult,
  DuplicateSurveyDocument,
  DuplicateSurveyMutationResult,
  FetchFailedSurveyMigrationTasksDocument,
  FetchFailedSurveyMigrationTasksQueryResult,
  FetchSurveyElementLanguagesDocument,
  FetchSurveyElementLanguagesQueryResult,
  FetchSurveyListWithTimezoneDocument,
  FetchSurveyWithSurveyElementsDocument,
  FetchSurveyWithSurveyElementsQueryResult,
  GenerateOpenSurveyAccessCodeDocument,
  GenerateOpenSurveyAccessCodeMutationResult,
  GetCompanyEmployeeIdsInSurveyDocument,
  MigrateSurveyDocument,
  MigrateSurveyMutationResult,
  SendTestSurveyDocument,
  SendTestSurveyMutationResult,
  SetOpenSurveyAccessCodeDocument,
  SetOpenSurveyAccessCodeMutationResult,
  SurveyMigrationTask,
  UpdateRecipientListDocument,
  UpdateSurveyDocument,
} from '~/gql/graphql';
import { fetchBasicSurveyListQuery } from '../react-query/survey';

export const CREATED = 'CREATED';

const fetchSurveyListWithTimezone = createAsyncThunk<SurveyDict, { companyId: number }>(
  'survey/fetchSurveyListWithTimezone',
  async (companyId) => {
    const data = await graphqlRequest<any>(FetchSurveyListWithTimezoneDocument, {
      companyId,
    });
    const surveyList = transformers.surveysFrom(data.data?.dashboardSurveys.results);
    const normalizedData = normalize(surveyList, [schemas.survey]);

    return normalizedData;
  },
);

// Fetch only the id + surveyName attributes for all surveys with `companyId`.
const fetchBasicSurveyList = createAsyncThunk<SurveyDict, number>('survey/fetchBasicSurveyList', async (companyId) => {
  const surveyList = await fetchBasicSurveyListQuery(companyId);
  const normalizedData = normalize(surveyList, [schemas.survey]);

  return normalizedData;
});

const createSurvey = createAsyncThunk<SurveyDict, CreateSurveyInput>(
  'survey/createSurvey',
  async (
    {
      companyId,
      surveyTypeId,
      name,
      languageCodes,
      showResults,
      enableConversations,
      combineLanguages,
      emailLanguages,
      isContinuous,
      lifecycleSurveyTypeId,
      clientTimezone,
      audienceMode,
    },
    { rejectWithValue },
  ) => {
    const body: CreateSurveyInput = {
      companyId,
      surveyTypeId,
      name,
      languageCodes,
      showResults,
      enableConversations,
      combineLanguages,
      isContinuous,
      lifecycleSurveyTypeId,
      clientTimezone,
      audienceMode,
    };

    if (emailLanguages?.length) {
      body['emailLanguages'] = emailLanguages;
    }

    const url = `${host}/surveys`;
    const res = await httpRequest<any>(url, {
      method: 'POST',
      body,
    });

    if (res.error) {
      // refactor: let backend send translated code
      const message = res.error.code === 'PULSV0001' ? languages.t('admin.survey.duplicated') : res.error.message;
      return rejectWithValue({
        ...res.error,
        message,
      });
    }

    const survey = transformers.surveyFrom(res.payload?.survey);

    const normalizedData = normalize(survey, schemas.survey);

    return {
      ...normalizedData,
      surveyId: survey.id,
    };
  },
);

const cancelScheduleSurvey = createAsyncThunk<
  SurveyDict,
  { surveyId: number; companyId: number; surveyTypeId: number }
>('survey/cancelScheduleSurvey', async (surveyData, { rejectWithValue }) => {
  const { surveyId, companyId, surveyTypeId } = surveyData;

  const url = `${host}/surveys/${surveyId}`;
  const res = await httpRequest<any>(url, {
    method: 'PATCH',
    body: {
      companyId,
      surveyTypeId,
      surveyStatusId: 1,
    },
  });

  if (res.error) return rejectWithValue(res.error);

  const survey = transformers.surveyFrom(res.payload?.survey);

  const normalizedData = normalize(survey, schemas.survey);
  return {
    ...normalizedData,
    surveyId: survey.id,
    title: 'Done',
    message: languages.t('admin.survey.cancelled'),
    status: statuses.success,
  };
});

const updateSurveyStatus = createAsyncThunk<SurveyDict | unknown, UpdateSurveyStatusInput>(
  'survey/updateSurvey',
  async (surveyData, { rejectWithValue }) => {
    const { surveyId, companyId, surveyTypeId, surveyStatusId } = surveyData;

    const url = `${host}/surveys/${surveyId}`;

    const res = await httpRequest<any>(url, {
      method: 'PATCH',
      body: {
        companyId,
        surveyTypeId,
        surveyStatusId,
      },
    });

    if (!res?.payload?.survey) return rejectWithValue(res);

    const survey = transformers.surveyFrom(res.payload?.survey);
    const normalizedData = normalize(survey, schemas.survey);
    return {
      ...normalizedData,
      isSuccess: true,
      surveyId: survey.id,
    };
  },
);

const updateSurvey = createAsyncThunk<SurveyDict | unknown, UpdateSurveyInput>(
  'survey/updateSurvey',
  async (surveyData, { rejectWithValue }) => {
    const {
      surveyId,
      companyId,
      surveyTypeId,
      surveyStatusId,
      name,
      languageCodes,
      showResults,
      enableConversations,
      emailAddressId,
      senderName,
      timezoneId,
      startTimeUTC,
      endTimeUTC,
      emailLanguages,
      employeeIds,
      isSaveSurvey,
      combineLanguages,
      disableEmailSending,
      invitationExpiryIn,
    } = surveyData;

    const url = `${host}/surveys/${surveyId}`;
    const res = await httpRequest<any>(url, {
      method: 'PATCH',
      body: {
        companyId,
        surveyTypeId,
        surveyStatusId,
        name,
        languageCodes,
        showResults,
        enableConversations,
        emailAddressId,
        senderName,
        timezoneId,
        startTimeUTC,
        endTimeUTC,
        emailLanguages,
        employeeIds,
        combineLanguages,
        disableEmailSending,
        invitationExpiryIn,
      },
    });

    if (res.error) {
      // refactor: let backend send translated code
      const message = res.error.code === 'PULSV0001' ? languages.t('admin.survey.duplicated') : res.error.message;
      return rejectWithValue({
        ...res.error,
        message,
      });
    }

    const survey = transformers.surveyFrom(res.payload?.survey);

    const normalizedData = normalize(survey, schemas.survey);
    return {
      ...normalizedData,
      isSuccess: true,
      surveyId: survey.id,
      title: isSaveSurvey ? 'Done' : null,
      message: isSaveSurvey ? languages.t('swal.message.surveySaved') : null,
      status: isSaveSurvey ? statuses.success : null,
    };
  },
);

const sendTestSurvey = createAsyncThunk<TestSurveyResult, { surveyId: number }>(
  'survey/sendTestSurvey',
  async ({ surveyId }, { rejectWithValue }) => {
    const res = await graphqlRequest<SendTestSurveyMutationResult>(SendTestSurveyDocument, { surveyId });
    if (hasGraphQLNetworkError(res)) return rejectWithValue(res.error);

    return {
      isSuccess: true,
      title: 'Done',
      message: languages.t('survey.send.test.success'),
      status: statuses.success,
    };
  },
);

const fetchSurveyWithSurveyElements = createAsyncThunk<SurveyDict, { surveyId: number }>(
  'survey/fetchSurveyWithSurveyElements',
  async ({ surveyId }, { rejectWithValue }) => {
    const res = await graphqlRequest<FetchSurveyWithSurveyElementsQueryResult>(FetchSurveyWithSurveyElementsDocument, {
      id: surveyId,
    });

    if (hasGraphQLNetworkError(res)) return rejectWithValue(res.error);

    const survey = transformers.surveyFromSurveyEdit(res.data?.survey);
    const normalizedSurveys = normalize([survey], [schemas.survey]);

    return normalizedSurveys;
  },
);

const exportToExcelSurveyElements = createAsyncThunk<
  void,
  {
    languageCodes: string[];
    surveyElements: SurveyElementToExport[];
    logicQuestionsIds: number[];
    companyName: string;
  }
>('survey/exportToExcelSurveyElements', async ({ languageCodes, surveyElements, logicQuestionsIds, companyName }) => {
  const rawSheets = languageCodes.map(function (languageCode) {
    const surveyElementFields = [
      excel.buildStringCell(languages.t('questionExport.questionNumber')),
      excel.buildStringCell(languages.t('questionExport.questionType')),
      excel.buildStringCell(languages.t('questionExport.FactorName')),
      excel.buildStringCell(languages.t('questionExport.Question')),
      excel.buildStringCell(languages.t('questionExport.Description')),
      excel.buildStringCell(languages.t('questionExport.responseOptions')),
      excel.buildStringCell(languages.t('questionExport.isQuestionRequired')),
      excel.buildStringCell(languages.t('questionExport.commentsTurnedOn')),
      excel.buildStringCell(languages.t('questionExport.isTrend')),
      excel.buildStringCell(languages.t('questionExport.isConditionalOnAttribute')),
      excel.buildStringCell(languages.t('questionExport.isConditionalOnPreviousQuestion')),
      excel.buildStringCell(languages.t('questionExport.isBenchmarked')),
      excel.buildStringCell(languages.t('questionExport.benchmarkedQuestion')),
    ];

    const surveyElementsData = surveyElements.map((surveyElement) => {
      const isMultipleChoice = surveyElement?.typeKey === typeNames.MULTIPLE;
      let optionsString = '';
      if (isMultipleChoice) {
        for (let i = 1; i <= surveyElement?.totalOptions; i++) {
          optionsString += surveyElement?.translations[languageCode]['option' + i];
          if (i !== surveyElement?.totalOptions) {
            optionsString += ' , ';
          }
        }
      }

      const languageId = surveyElement?.translations[languageCode]?.languageId;
      const benchmarkedQuestionText = surveyElement?.benchmarkQuestionTranslations?.find(
        (question) => question.languageId === languageId,
      );

      return [
        excel.buildStringCell(surveyElement?.order),
        excel.buildStringCell(surveyElement?.typeLabel),
        excel.buildStringCell(surveyElement?.categoryName),
        excel.buildStringCell(surveyElement?.translations[languageCode]?.title),
        surveyElement?.showDescription
          ? excel.buildStringCell(surveyElement?.translations[languageCode]?.desc)
          : excel.buildStringCell(''),
        excel.buildStringCell(optionsString),
        surveyElement?.isRequired
          ? excel.buildStringCell(languages.t('boolean.yes'))
          : excel.buildStringCell(languages.t('boolean.no')),
        surveyElement?.allowComments
          ? excel.buildStringCell(languages.t('boolean.yes'))
          : excel.buildStringCell(languages.t('boolean.no')),
        surveyElement?.isPreviousQuestion
          ? excel.buildStringCell(languages.t('boolean.yes'))
          : excel.buildStringCell(languages.t('boolean.no')),
        surveyElement?.isAttributeBased
          ? excel.buildStringCell(languages.t('boolean.yes'))
          : excel.buildStringCell(languages.t('boolean.no')),
        logicQuestionsIds.includes(surveyElement?.id)
          ? excel.buildStringCell(languages.t('boolean.yes'))
          : excel.buildStringCell(languages.t('boolean.no')),
        surveyElement.isBenchmarkedQuestion
          ? excel.buildStringCell(languages.t('boolean.yes'))
          : excel.buildStringCell(languages.t('boolean.no')),
        surveyElement.isBenchmarkedQuestion
          ? excel.buildStringCell(benchmarkedQuestionText?.questionText)
          : excel.buildStringCell(''),
      ];
    });

    return {
      title: languageCode,
      rows: [surveyElementFields, ...surveyElementsData],
    };
  });

  const titles = rawSheets.map((sheet) => sheet.title);
  const sheetTitles = excel.buildSheetTitles(titles);
  const sheetRows = rawSheets.map((sheet) => sheet.rows);
  const fileName = `${companyName} - ${languages.t('questions')}`;
  excel.download(fileName, sheetTitles, sheetRows);
});

const fetchBenchmarkedQuestionTranslations = createAsyncThunk<
  { surveyElementId: number; surveyElementLanguages: unknown[] } | unknown,
  { surveyElementId: number }
>('survey/fetchBenchmarkedQuestionTranslations', async ({ surveyElementId }, { rejectWithValue }) => {
  const res = await graphqlRequest<FetchSurveyElementLanguagesQueryResult>(FetchSurveyElementLanguagesDocument, {
    surveyElementLanguagesId: surveyElementId,
    benchmarkedQuestionLanguages: true,
  });
  if (hasGraphQLErrors(res)) return rejectWithValue(res.errors[0]);
  return { surveyElementId, surveyElementLanguages: res.data?.surveyElementLanguages };
});

const deleteSurvey = createAsyncThunk<
  { surveyId: number } | unknown,
  { surveyId: number; companyId: number; languageId: number }
>('legacySurvey/deleteSurvey', async ({ surveyId, companyId, languageId }, { rejectWithValue }) => {
  const params = {
    surveyId,
    companyId,
    languageId,
  };
  const data = await graphqlRequest<DeleteSurveyMutationResult>(DeleteSurveyDocument, params);

  if (hasGraphQLErrors(data)) return rejectWithValue(data.errors[0]);

  if (!data.data?.deleteSurvey) return;

  return { surveyId };
});

const duplicateSurvey = createAsyncThunk<
  number[],
  { surveyId: number; surveyName: string; companyId: number; languageId: number }
>('legacySurvey/duplicateSurvey', async ({ surveyId, surveyName, companyId, languageId }, { rejectWithValue }) => {
  const params = {
    surveyId,
    surveyName,
    companyId,
    languageId,
    clientTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
  let data;
  try {
    data = await graphqlRequest<DuplicateSurveyMutationResult>(DuplicateSurveyDocument, params);
    if (hasGraphQLErrors(data)) return rejectWithValue(data.errors[0]);

    return data.data?.duplicateSurvey;
  } catch (errorMessage) {
    if (errorMessage) {
      // refactor: let backend send translated code
      if (errorMessage === 'Survey Name Already Taken') {
        return rejectWithValue({ message: languages.t('admin.survey.duplicated') });
      }

      return rejectWithValue({ message: errorMessage });
    }
  }
});

const cancelSurveySchedule = createAsyncThunk<SurveyDict, { surveyId: number }>(
  'survey/cancelSurveySchedule',
  async (surveyId, { rejectWithValue }) => {
    const data = await graphqlRequest<any>(UpdateSurveyDocument, {
      id: surveyId,
      survey: {
        status: { name: CREATED },
      },
    });

    if (hasGraphQLErrors(data)) return rejectWithValue(data.errors[0]);

    return data.data?.updateSurvey;
  },
);

const updateRecipientList = createAsyncThunk<SurveyDict, { surveyId: number; employeeIds: number[] }>(
  'survey/updateRecipientList',
  async ({ surveyId, employeeIds }, { rejectWithValue }) => {
    const data = await graphqlRequest<any>(UpdateRecipientListDocument, {
      surveyId,
      employeeIds,
    });

    if (hasGraphQLErrors(data)) return rejectWithValue(data.errors[0]);

    return {
      entities: {
        surveys: [
          {
            id: surveyId,
            recipientIds: data.data?.updateRecipientList.employees.map((employee) => employee.id),
            recipientInvitationDates: convert.toDictValue(
              data.data?.updateRecipientList.employees,
              'id',
              'surveyInviteDate',
            ),
            recipientAddedDates: convert.toDictValue(data.data?.updateRecipientList.employees, 'id', 'addedDate'),
          },
        ],
      },
    };
  },
);

const getSurveyDataFromFile = (fileBuffer: Buffer) => {
  const jsonData = excel.toJson(fileBuffer);
  const surveyData = getMigrationSurveyData(jsonData);
  return surveyData;
};

const validateSurveyData = createAction(
  'survey/validateSurveyData',
  ({ surveyId, fileBuffer, attributeNames, surveyElements, employeeIds, companyEmployeeIdsInSurvey }) => {
    const surveyData = getSurveyDataFromFile(fileBuffer);
    const errors: any = validateSurveyMetadata({
      surveyData,
      attributeNames,
      surveyElements,
      employeeIds,
      companyEmployeeIdsInSurvey,
    });

    let errorMessage = '';
    const nonArrayErrors = [
      'noEmployeeIdColumn',
      'noCompletedSurveyColumn',
      'unsupportedEmployeeId',
      'unsupportedCompletedSurvey',
      'noInvitationTimeColumn',
      'noSubmissionDateColumn',
    ];

    const isValid = !Object.keys(errors).length;
    if (!isValid) {
      errorMessage = Object.keys(errors).reduce((accMsg, key) => {
        if (errors[key].length === 0) return accMsg;

        const msgGroupHeader = l10n.t(`survey.migrate.error.${key}`);
        if (nonArrayErrors.includes(key)) {
          return accMsg + `■ ${msgGroupHeader} \n\n`;
        }
        if (key === 'missingMultipleChoiceOptions') {
          let missingOptionsMsg = accMsg + `■ ${msgGroupHeader}:\n`;
          Object.keys(errors[key]).forEach((question) => {
            const questionMessage = errors[key][question].reduce((acc, option) => {
              return acc + `  - ${option}\n`; // separate each error message with a line break and a bullet point
            }, `- ${question}:\n`);
            return (missingOptionsMsg += questionMessage);
          });
          return missingOptionsMsg + '\n';
        }
        const msgGroup = errors[key].reduce((acc, msg) => {
          return acc + `- ${msg}\n`; // separate each error message with a line break and a bullet point
        }, `${msgGroupHeader}:\n`);

        return accMsg + `■ ${msgGroup}\n`; // add a line break between groups of error messages
      }, '');
    }

    return { payload: { isValid, errorMessage } };
  },
);

const migrateSurvey = createAsyncThunk<
  Notification | unknown,
  { surveyConfig: SurveyConfig; surveyFile: File; overwriteSurveyData: boolean },
  { state: RootState }
>(
  'survey/migrateSurveyFromFile',
  async ({ surveyConfig, surveyFile, overwriteSurveyData }, { rejectWithValue, getState }) => {
    const state = getState();

    const formData = new FormData();
    formData.append('survey_file', surveyFile);
    const uploadUrl = `${host}/surveys/migrate/${surveyConfig.surveyId}`;
    const uploadResponse = await httpRequest<any>(uploadUrl, {
      method: 'POST',
      body: formData,
      skipContentType: true,
    });

    if (uploadResponse?.errors || !uploadResponse?.payload?.surveyFileUrl) {
      if (uploadResponse?.errors) console.log(uploadResponse.errors);
      return rejectWithValue('Survey file upload failed');
    }
    const surveyFileUrl = uploadResponse.payload.surveyFileUrl;

    const res = await graphqlRequest<MigrateSurveyMutationResult>(MigrateSurveyDocument, {
      companyId: state.account?.companyId,
      surveyConfig,
      fileUrl: surveyFileUrl,
      overwriteSurveyData,
    });
    if (hasGraphQLErrors(res)) return rejectWithValue(res.errors[0]);

    return {
      migratedSurvey: res.data?.migrateSurvey,
      notification: {
        title: l10n.t('notifications.title.survey-migration'),
        message: l10n.t('survey.migrate.message.scheduled'),
        status: statuses.success,
      },
    };
  },
);

const genSurveyMigrationTemplate = createAction(
  'survey/genSurveyMigrationTemplate',
  ({ attributeData, questionData }) => {
    const fileName = 'survey_migration_template';
    const questionPrefix = 'answer:::';
    const commentPrefix = 'comment:::';
    const completedSurveyTitle = 'Completed Survey';
    const invitationTimeTitle = 'Invitation Time';
    const submissionDateTitle = 'Submission Date';
    const completedSurveySampleValue = 'Yes';
    const tabName = 'Survey Data';
    const invitationTimeSampleValue = '2021-01-01 12:00:00';
    const submissionDateSampleValue = '2021-01-15 09:30:00';

    const attributeTitles = attributeData.map((attribute) => text.toTitlecase(attribute.name));
    const attributeSampleValues = attributeData.map((attribute) => convert.attributeTypeToSampleValue(attribute.type));
    const questionTitles = questionData.map((question) => questionPrefix + question.title);
    const commentTitles = questionData
      .filter((question) => question.allowComments)
      .map((question) => commentPrefix + question.title);

    const surveyDataTab = {
      name: tabName,
      titles: [
        completedSurveyTitle,
        invitationTimeTitle,
        submissionDateTitle,
        ...attributeTitles,
        ...questionTitles,
        ...commentTitles,
      ],
      rows: [
        [completedSurveySampleValue, invitationTimeSampleValue, submissionDateSampleValue, ...attributeSampleValues],
      ],
    };

    // Will trigger browser download the sample file
    excel.toFile(fileName, [surveyDataTab]);

    return { payload: { fileName } };
  },
);

const approveMigratedSurvey = createAsyncThunk<MigratedSurvey | unknown, { surveyId: number }>(
  'survey/approveMigratedSurvey',
  async ({ surveyId }, { rejectWithValue }) => {
    const res = await graphqlRequest<ApproveMigratedSurveyMutationResult>(ApproveMigratedSurveyDocument, { surveyId });
    if (hasGraphQLErrors(res)) return rejectWithValue(res.errors[0]);

    return { migratedSurvey: res.data?.approveMigratedSurvey };
  },
);

const fetchFailedSurveyMigrationTasks = createAsyncThunk<SurveyMigrationTask[] | unknown, { companyId: number }>(
  'survey/fetchFailedSurveyMigrationTasks',
  async ({ companyId }, { rejectWithValue }) => {
    const res = await graphqlRequest<FetchFailedSurveyMigrationTasksQueryResult>(
      FetchFailedSurveyMigrationTasksDocument,
      { companyId },
    );
    if (hasGraphQLErrors(res)) return rejectWithValue(res.errors[0]);

    return { failedSurveyMigrationTasks: res.data?.getFailedSurveyMigrationTasks };
  },
);

// This action help us to store the Survey Style for a survey that is being created.
const setTemporalSurveyStyle = createAction('survey/setTemporalSurveyStyle', ({ isContinuous }) => {
  return { payload: { isContinuous } };
});

const fetchCompanyEmployeeIdsInSurvey = createAsyncThunk<string[], { surveyId: number }>(
  'survey/fetchCompanyEmployeeIdsInSurvey',
  async ({ surveyId }, { rejectWithValue }) => {
    const res = await graphqlRequest<any>(GetCompanyEmployeeIdsInSurveyDocument, {
      surveyId,
    });
    if (hasGraphQLErrors(res)) return rejectWithValue(res.errors[0]);

    return res.data?.getCompanyEmployeeIdsInSurvey;
  },
);

const convertToContinuous = createAsyncThunk<
  { surveyId: number; isContinuous: boolean; lifecycleSurveyTypeId: number },
  { surveyId: number; continuousSurveyType: string }
>('survey/convertSurveyToContinuous', async ({ surveyId, continuousSurveyType }, { rejectWithValue }) => {
  const res = await graphqlRequest<any>(ConvertSurveyToContinuousDocument, {
    surveyId,
    continuousSurveyType,
  });
  if (hasGraphQLErrors(res)) return rejectWithValue(res.errors[0]);

  return res.data?.convertSurveyToContinuous;
});

// Re-enabling mints a fresh code, which is the only way to revoke a leaked one. The survey's
// link is untouched either way.
const toggleOpenSurveyAccessCode = createAsyncThunk<SurveyDict, { surveyId: number; enabled: boolean }>(
  'survey/toggleOpenSurveyAccessCode',
  async ({ surveyId, enabled }, { rejectWithValue }) => {
    const document = enabled ? GenerateOpenSurveyAccessCodeDocument : DisableOpenSurveyAccessCodeDocument;
    const res = await graphqlRequest<
      GenerateOpenSurveyAccessCodeMutationResult | DisableOpenSurveyAccessCodeMutationResult
    >(document, { surveyId });

    if (hasGraphQLNetworkError(res)) return rejectWithValue(res.error);
    if (hasGraphQLErrors(res)) return rejectWithValue(res.errors[0]);

    const survey =
      res.data && 'generateOpenSurveyAccessCode' in res.data
        ? res.data.generateOpenSurveyAccessCode
        : res.data?.disableOpenSurveyAccessCode;

    // Only id + openSurveyAccessCode come back; the reducer merges, so the rest of the entity stays.
    return normalize(survey, schemas.survey);
  },
);

const setOpenSurveyAccessCode = createAsyncThunk<SurveyDict, { surveyId: number; accessCode: string }>(
  'survey/setOpenSurveyAccessCode',
  async ({ surveyId, accessCode }, { rejectWithValue }) => {
    const res = await graphqlRequest<SetOpenSurveyAccessCodeMutationResult>(SetOpenSurveyAccessCodeDocument, {
      surveyId,
      accessCode,
    });

    if (hasGraphQLNetworkError(res)) return rejectWithValue(res.error);
    if (hasGraphQLErrors(res)) return rejectWithValue(res.errors[0]);

    return normalize(res.data?.setOpenSurveyAccessCode, schemas.survey);
  },
);

export const surveyActions = {
  fetchSurveyListWithTimezone,
  fetchBasicSurveyList,
  fetchSurveyWithSurveyElements,
  createSurvey,
  updateSurvey,
  updateSurveyStatus,
  cancelScheduleSurvey,
  sendTestSurvey,
  exportToExcelSurveyElements,
  fetchBenchmarkedQuestionTranslations,
  deleteSurvey,
  duplicateSurvey,
  cancelSurveySchedule,
  validateSurveyData,
  migrateSurvey,
  genSurveyMigrationTemplate,
  approveMigratedSurvey,
  fetchFailedSurveyMigrationTasks,
  setTemporalSurveyStyle,
  updateRecipientList,
  fetchCompanyEmployeeIdsInSurvey,
  convertToContinuous,
  toggleOpenSurveyAccessCode,
  setOpenSurveyAccessCode,
};
